import React, {
  ReactNode,
  useState,
  useEffect,
  KeyboardEvent,
  MouseEvent,
} from 'react';

import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { Fade, Menu } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { useAbility } from '@casl/react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Icon } from '@vk-hr-tek/ui/Icon';

import { AppAbilities } from '@app/ability/ability';
import { AbilityContext } from '@app/ability/Can';

import {
  TIMEOUT_FOR_FADE,
  TIMEOUT_FOR_SUBMENU_HOVER,
  ITEM_HEIGHT,
} from '../Sidebar.constants';

import { useStyles } from './SidebarItemsWithSubmenu.styles';
import { SubmenuItem } from './SubmenuItem';

interface SidebarItemsWithSubmenuProps {
  icon: ReactNode;
  title: string;
  sidebarOpened: boolean;
  isActive: boolean;
  maxItemsInMenu?: number;
  closeDrawer?: (event: KeyboardEvent | MouseEvent) => void;
  isMobile?: boolean;
  skeletonLoading: boolean;
  aqaClass: string;
  setSubmenusVisibleItemsCount?: (count: number) => void;
  role?: string;
  onMenuItemClick: (pageTitle: string) => void;
  submenu: {
    aqaClass: string;
    title: string;
    href: string;
    resource: AppAbilities[1];
    isCustom?: boolean;
    external_system?: boolean;
    blank?: boolean;
  }[];
}

export const SidebarItemsWithSubmenu = ({
  icon,
  title,
  sidebarOpened,
  submenu,
  isActive,
  skeletonLoading,
  closeDrawer,
  aqaClass,
  isMobile = false,
  role,
  setSubmenusVisibleItemsCount,
  onMenuItemClick,
}: SidebarItemsWithSubmenuProps) => {
  const { pathname } = useLocation();
  const ability = useAbility(AbilityContext);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<boolean>(false);

  const filteredSubmenu = submenu.filter((item) => {
    return ability.can('read', item.resource as AppAbilities[1]);
  });

  const submenuHeight = expanded ? filteredSubmenu.length * ITEM_HEIGHT : 0;

  const { classes } = useStyles({
    submenuHeight,
    expanded,
  });

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const renderAqaClass = (className: string): string =>
    `${aqaClass}_${className}`;

  const handleIsActive = (
    path: string,
    href: string,
    userRole: string,
    isCustomItem = false,
  ): boolean => {
    if (isCustomItem) {
      return path === href;
    }

    if (href === `/${userRole}/settings`) {
      return path === `/${userRole}/settings`;
    }

    if (href === `/${userRole}/events`) {
      return (
        path === `/${userRole}/events` ||
        (path.startsWith(`/${userRole}/events/`) && !path.includes('/paper'))
      );
    }

    if (href === `/${userRole}/settings/attorneys`) {
      return (
        path === `/${userRole}/settings/attorneys` ||
        (path.startsWith(`/${userRole}/settings/attorneys/`) &&
          !path.includes('/attorneys-users'))
      );
    }

    return (
      path === href ||
      path === `/employee${href}` ||
      path.startsWith(`${href}/`) ||
      path.startsWith(`/employee${href}/`)
    );
  };

  const handleExpandInnerMenu = () => {
    setExpanded((prev) => !prev);

    if (!expanded) {
      setSubmenusVisibleItemsCount?.(filteredSubmenu.length);
    } else {
      setSubmenusVisibleItemsCount?.(-filteredSubmenu.length);
    }
  };

  const handleOpenMenu = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseHover = () => {
    setTimeout(() => {
      handleCloseMenu();
    }, TIMEOUT_FOR_SUBMENU_HOVER);
  };

  const open = Boolean(anchorEl);

  const hoverOnMenuItem = !sidebarOpened ? handleOpenMenu : undefined;

  const activeStyleWhenSidebarIsClose =
    (!expanded || !sidebarOpened) && isActive && classes.active;

  useEffect(() => {
    if (sidebarOpened && isActive) {
      setExpanded(true);
      setSubmenusVisibleItemsCount?.(filteredSubmenu.length);
    } else {
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpened]);

  const renderSubmenuItems = (handleClose?: () => void) =>
    filteredSubmenu.map(
      ({
        title: submenuTitle,
        href,
        aqaClass: aqaSubmenuClass,
        isCustom = false,
        external_system: externalSystem = false,
        blank = false,
      }) => {
        const userRole = role ? role : href.split('/')[1];
        const isSubmenuCustom = isCustom || (externalSystem && blank);

        const isActiveItem = handleIsActive(
          pathname,
          href,
          userRole,
          isSubmenuCustom,
        );

        return (
          <SubmenuItem
            key={submenuTitle}
            title={submenuTitle}
            href={href}
            aqaClass={aqaSubmenuClass}
            isActive={isActiveItem}
            isCustom={isSubmenuCustom}
            external={externalSystem}
            blank={blank}
            closeDrawer={closeDrawer}
            onMenuItemClick={onMenuItemClick}
            handleClose={handleClose}
            sidebarOpened={sidebarOpened}
            classes={classes}
          />
        );
      },
    );

  return (
    <Box
      className={classes.menuItemWithSubmenu}
      onMouseEnter={hoverOnMenuItem}
      onMouseLeave={handleCloseHover}
    >
      <Box
        className={classNames(
          classes.menuItem,
          activeStyleWhenSidebarIsClose,
          renderAqaClass('menuItemWithSubmenu'),
        )}
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        aria-haspopup="true"
        onClick={sidebarOpened ? handleExpandInnerMenu : hoverOnMenuItem}
      >
        <Box
          minWidth={24}
          minHeight={24}
          display="flex"
          alignItems="center"
          justifyContent="center"
          className="aqa_menuItemIcon"
        >
          {skeletonLoading ? <Skeleton width="100%" /> : icon}
        </Box>

        {!sidebarOpened && (
          <Fade in={!sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
            <Box
              className={classNames(
                classes.absoluteIcon,
                renderAqaClass('absoluteIcon'),
              )}
            >
              <Icon name="dropdown_outline" size="12" />
            </Box>
          </Fade>
        )}

        <Fade in={sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
          <Box minWidth={0}>
            <Typography
              variant="body3"
              className={classNames(
                renderAqaClass('menuItemText'),
                classes.truncatedText,
              )}
            >
              {skeletonLoading ? <Skeleton width={200} /> : title}
            </Typography>
          </Box>
        </Fade>

        {sidebarOpened && (
          <Box
            ml="auto"
            display="flex"
            alignItems="center"
            className={classNames(
              classes.menuItemExpandIcon,
              renderAqaClass('menuItemExpandIcon'),
            )}
          >
            <Icon name="dropdown" size="16" />
          </Box>
        )}
      </Box>

      <Box
        className={classNames(
          classes.dropdownMenu,
          renderAqaClass('dropdownMenu'),
        )}
      >
        {renderSubmenuItems()}
      </Box>

      {!sidebarOpened && !isMobile && (
        <Menu
          id="simple-menu"
          className={classNames(
            classes.submenu,
            renderAqaClass('popupSubmenu'),
          )}
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          MenuListProps={{ autoFocusItem: false }}
          slotProps={{
            root: {
              disableAutoFocus: true,
              disableEnforceFocus: true,
              disableRestoreFocus: true,
            },
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PopoverClasses={{
            root: classes.popOverRoot,
          }}
        >
          {renderSubmenuItems(handleCloseMenu)}
        </Menu>
      )}
    </Box>
  );
};
